import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { iceLogo } from '../mock';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  { to: '/team', label: 'Our Members' },
  { to: '/alumni', label: 'Alumni' },
  { to: '/scholars', label: 'Scholars' },
  { to: '/events', label: 'Events' },
  { to: '/announcements', label: 'Announcements' },
  { to: '/football-practice-times', label: 'Football Practice Time' },
  { to: '/sponsors', label: 'Sponsors' },
  { to: '/apparel', label: 'Shop' },
  { to: '/contact', label: 'Contact' }
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-300 ${scrolled || open ? 'bg-[#0a1f44]/95 backdrop-blur-md shadow-lg py-2' : 'bg-[#0a1f44]/90 backdrop-blur-md py-2 sm:py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between min-h-12">
        <Link to="/" data-testid="navbar-home-logo" className="flex items-center gap-3 group min-w-0">
          <div className="relative">
            <img src={iceLogo} alt="ICE Brookline JP Patriots" className="h-10 sm:h-12 w-auto object-contain drop-shadow-lg group-hover:scale-105 transition-transform" />
          </div>
          <div className="leading-none hidden sm:block">
            <div className="font-display text-white text-xl tracking-wider leading-tight">BROOKLINE JAMAICA PLAIN</div>
            <div className="font-heading text-[#c8102e] text-[10px] tracking-[0.25em] mt-1">POP WARNER FOOTBALL &amp; CHEERLEADING</div>
          </div>
        </Link>

        <nav className="hidden 2xl:flex items-center gap-0.5">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={`navbar-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `px-3 py-2 font-heading tracking-wider text-xs uppercase transition-colors relative group ${
                  isActive ? 'text-[#c8102e]' : 'text-white hover:text-[#c8102e]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute left-4 right-4 -bottom-0.5 h-0.5 bg-[#c8102e] transition-transform origin-left ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
                </>
              )}
            </NavLink>
          ))}
          <Link to="/register" data-testid="navbar-register-btn" className="ml-4 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading tracking-wider text-sm uppercase px-5 py-2.5 transition-colors">
            Register
          </Link>
        </nav>

        <button className="2xl:hidden text-white w-11 h-11 flex items-center justify-center" onClick={() => setOpen(!open)} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="mobile-navigation" data-testid="navbar-mobile-menu">
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile */}
      {open && (
        <div id="mobile-navigation" className="2xl:hidden bg-[#0a1f44]/98 backdrop-blur-xl border-t border-white/10 max-h-[calc(100dvh-64px)] overflow-y-auto overscroll-contain">
          <div className="px-4 py-3 flex flex-col gap-1 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                onClick={() => setOpen(false)}
                data-testid={`mobile-navbar-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `min-h-12 py-3 px-2 flex items-center font-heading uppercase border-b border-white/10 ${isActive ? 'text-[#c8102e]' : 'text-white'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/register" onClick={() => setOpen(false)} data-testid="mobile-navbar-register-btn" className="mt-3 min-h-12 bg-[#c8102e] text-white font-heading uppercase flex items-center justify-center px-5 py-3">
              Register Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
